// 观点：三个页签（我的判断 · 观点 · 主题），每个都是左边列表、右边选中那条的详情。数据多的时候靠搜索、筛选、分组找。
// 「相近」在同一篇资料里按字面算（不调模型），详情里可以合并成一条；「写一条判断」是单独的一屏。
// 服务端是 lingzhi-studio。看、找都不调用模型；只有点「整理观点」才调用，每天有上限。

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App as AntApp, Dropdown, type MenuProps } from 'antd'
import { ArrowLeft, ArrowUp, BookOpen, Check, ChevronDown, ChevronRight, FileText, Lightbulb, Plus, RotateCcw, Search, Trash2, X } from 'lucide-react'
import {
  addFeedback,
  cancelJob,
  CLAIM_KIND_LABEL,
  createJudgment,
  deleteJudgment,
  docInsights,
  extractClaims,
  getJudgment,
  getTopic,
  insightsMeta,
  listClaims,
  listJudgments,
  listRelations,
  listTopics,
  mergeClaims,
  pickClaim,
  pickPassage,
  relate,
  RELATION_LABEL,
  searchEvidence,
  syncJudgment,
  unrelate,
  type Claim,
  type ClaimKind,
  type Judgment,
  type Picked,
  type Relation,
  type RelationType,
} from '../../api/insights'
import { IconButton, Pill, Segmented } from '../../ui'
import { alike } from '../../lib/alike'
import { AttachPicker } from '../kb/AttachPicker'
import { cleanTitle } from '../kb/DocCard'
import { useDeleteClaim } from './useDeleteClaim'
import { useDocPreview } from '../kb/usePreview'
import '../chat/chat.css'
import '../kb/attach.css'
import './insights.css'
import { MicButton, useDictation } from '../voice/VoiceInput'

type Tab = 'judgments' | 'claims' | 'topics'
const day = (iso: string) => new Intl.DateTimeFormat('zh-CN', { timeZone: 'Asia/Shanghai', month: 'numeric', day: 'numeric' }).format(new Date(iso))
const keyOf = (p: { kind: string; id: string }) => `${p.kind}:${p.id}`
type Evidence = Extract<Picked, { kind: 'passage' | 'claim' }>

/** 同一篇里「相近」的观点：引用几乎一样，或者概括字面上很像（2026-10-11 真实数据里重复的几对引用都是 1.00、概括 0.61–0.76）。 */
const NEAR = { quote: 0.8, text: 0.6 }
function nearMap(claims: Claim[]) {
  const byDoc = new Map<string, Claim[]>()
  for (const c of claims) byDoc.set(c.knowledgeId, [...(byDoc.get(c.knowledgeId) ?? []), c])
  const near = new Map<string, string[]>()
  const add = (a: string, b: string) => near.set(a, [...(near.get(a) ?? []), b])
  for (const list of byDoc.values())
    for (let i = 0; i < list.length; i++)
      for (let j = i + 1; j < list.length; j++)
        if (alike(list[i].quote, list[j].quote) >= NEAR.quote || alike(list[i].text, list[j].text) >= NEAR.text) {
          add(list[i].id, list[j].id)
          add(list[j].id, list[i].id)
        }
  return near
}

/** 宽屏没选中时默认看第一条；窄屏先看列表。 */
const wide = () => typeof window.matchMedia !== 'function' || !window.matchMedia('(max-width: 900px)').matches

function useInsightsData() {
  // 别处（知识库里记为观点、整理观点，思考讨论定稿）也会改这些：进来就重新拉一次
  const claims = useQuery({ queryKey: ['insight-claims'], queryFn: listClaims, staleTime: 0 })
  const judgments = useQuery({ queryKey: ['insight-judgments'], queryFn: listJudgments, staleTime: 0 })
  const topics = useQuery({ queryKey: ['insight-topics'], queryFn: listTopics, staleTime: 0 })
  const near = useMemo(() => nearMap(claims.data ?? []), [claims.data])
  const cited = useMemo(() => {
    const m = new Map<string, number>()
    for (const j of judgments.data ?? []) for (const e of j.evidence) if (e.kind === 'claim') m.set(e.id, (m.get(e.id) ?? 0) + 1)
    return m
  }, [judgments.data])
  return { claims, judgments, topics, near, cited }
}
type Data = ReturnType<typeof useInsightsData>

/** 写一条判断的草稿：切页签时留着。 */
interface Draft {
  topic: string
  reasoning: string
  evidence: Evidence[]
}

export function InsightsPage() {
  const [params, setParams] = useSearchParams()
  const latestParams = useRef(params)
  const renderedParams = useRef(params)
  // 数据刷新可能先于导航重新渲染；相同 URL 不能覆盖已排队的目标参数。
  if (renderedParams.current !== params) {
    renderedParams.current = params
    latestParams.current = params
  }
  const tab: Tab = params.get('tab') === 'claims' || params.get('tab') === 'topics' ? (params.get('tab') as Tab) : 'judgments'
  const activeTab = useRef<Tab>(tab)
  useEffect(() => { activeTab.current = tab }, [tab])
  const d = useInsightsData()
  const [draft, setDraft] = useState<Draft>({ topic: '', reasoning: '', evidence: [] })
  // React Router 的 setSearchParams 函数式更新不排队：同一轮的两个写入必须从最新目标合并。
  const go = (next: Record<string, string | undefined>, replace = false) => {
    if (next.tab === 'claims' || next.tab === 'topics' || next.tab === 'judgments') activeTab.current = next.tab
    const n = new URLSearchParams(latestParams.current)
    for (const [k, v] of Object.entries(next)) {
      if (v === undefined) n.delete(k)
      else n.set(k, v)
    }
    latestParams.current = n
    setParams(n, { replace })
  }
  const picked = (id: string) => draft.evidence.some((e) => keyOf(e) === `claim:${id}`)
  const toggle = (e: Evidence) => setDraft((x) => ({ ...x, evidence: x.evidence.some((y) => keyOf(y) === keyOf(e)) ? x.evidence.filter((y) => keyOf(y) !== keyOf(e)) : [...x.evidence, e] }))
  const compose = (patch: Partial<Draft> = {}) => {
    setDraft((x) => ({ ...x, ...patch, evidence: patch.evidence ? [...x.evidence, ...patch.evidence.filter((e) => !x.evidence.some((y) => keyOf(y) === keyOf(e)))] : x.evidence }))
    go({ compose: '1' })
  }

  if (params.get('compose'))
    return (
      <ComposeJudgment
        draft={draft}
        onDraft={setDraft}
        onBack={() => go({ compose: undefined })}
        onPickMore={() => go({ compose: undefined, tab: 'claims' })}
        onSaved={(j) => {
          setDraft({ topic: '', reasoning: '', evidence: [] })
          go({ compose: undefined, tab: 'judgments', sel: j.id })
        }}
      />
    )

  const counts = { judgments: d.judgments.data?.length, claims: d.claims.data?.length, topics: d.topics.data?.length }
  const tabs: { value: Tab; label: string }[] = [
    { value: 'judgments', label: `我的判断${counts.judgments ? ` ${counts.judgments}` : ''}` },
    { value: 'claims', label: `观点${counts.claims ? ` ${counts.claims}` : ''}` },
    { value: 'topics', label: `主题${counts.topics ? ` ${counts.topics}` : ''}` },
  ]
  return (
    <div className="ins">
      <header className="ins-head">
        <h1>观点</h1>
        <Segmented label="看什么" items={tabs} value={tab} onChange={(v) => go({ tab: v, sel: undefined, in: undefined, extract: undefined, think: undefined })} />
        <span className="grow" />
        <Pill variant="primary" icon={<Plus />} onClick={() => compose()}>
          写一条判断{draft.evidence.length ? ` · 依据 ${draft.evidence.length}` : ''}
        </Pill>
      </header>
      {tab === 'judgments' && <JudgmentsTab d={d} sel={params.get('sel')} think={params.get('think')} onSel={(id) => { if (activeTab.current === 'judgments') go({ sel: id, think: undefined }, true) }} />}
      {tab === 'claims' && (
        <ClaimsTab
          d={d}
          sel={params.get('sel')}
          onSel={(id) => { if (activeTab.current === 'claims') go({ sel: id, extract: undefined }, true) }}
          inDoc={params.get('in')}
          onInDoc={(id) => go({ in: id, sel: undefined, extract: undefined })}
          extract={params.get('extract')}
          onExtract={(id) => go({ extract: id, sel: undefined })}
          onTopic={(t) => go({ tab: 'topics', sel: t, in: undefined, extract: undefined })}
          picked={picked}
          toggle={toggle}
          onCompose={(c) => compose({ evidence: [pickClaim(c) as Evidence] })}
        />
      )}
      {tab === 'topics' && <TopicsTab d={d} sel={params.get('sel')} onSel={(t) => { if (activeTab.current === 'topics') go({ sel: t }, true) }} onClaim={(id) => go({ tab: 'claims', sel: id })} onCompose={(topic) => compose({ topic })} />}
    </div>
  )
}

// ———— 共用的小件 ————

function FilterMenu<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: [T, string][]; onChange: (v: T) => void }) {
  const current = options.find(([k]) => k === value)?.[1] ?? ''
  const menu: MenuProps = { items: options.map(([k, l]) => ({ key: k, label: l })), selectable: true, selectedKeys: [value], onClick: ({ key }) => onChange(key as T) }
  return (
    <Dropdown trigger={['click']} menu={menu}>
      <Pill size="sm" variant={value === options[0][0] ? 'ghost' : 'selected'} aria-label={`${label}：${current}`}>
        {current}
        <ChevronDown width={14} height={14} />
      </Pill>
    </Dropdown>
  )
}

function Toolbar({ q, onQ, children, count }: { q: string; onQ: (q: string) => void; children?: ReactNode; count: string }) {
  return (
    <div className="ins-tools">
      <label className="sk-search ins-q">
        <Search width={15} height={15} />
        <input value={q} onChange={(e) => onQ(e.target.value)} placeholder="搜索" aria-label="搜索" />
      </label>
      {children}
      <span className="count">{count}</span>
    </div>
  )
}

/** 左边列表 + 右边详情。窄屏选中了就只看详情（上面有「返回」）。 */
function MasterDetail({ list, detail, hasSel, onBack, label }: { list: ReactNode; detail: ReactNode; hasSel: boolean; onBack: () => void; label: string }) {
  return (
    <div className="md" data-has-sel={hasSel}>
      <div className="master" role="region" aria-label={label}>
        {list}
      </div>
      <aside className="detail" aria-label="详情" aria-live="polite">
        {hasSel && (
          <button type="button" className="md-back" onClick={onBack}>
            <ArrowLeft width={15} height={15} />
            返回列表
          </button>
        )}
        {detail}
      </aside>
    </div>
  )
}

function OpenDoc({ knowledgeId, kbId, children }: { knowledgeId: string; kbId?: string; children: ReactNode }) {
  const { open } = useDocPreview()
  return (
    <button type="button" className="ins-src" onClick={() => open(knowledgeId, kbId)}>
      <FileText width={13} height={13} />
      {children}
    </button>
  )
}

function Note({ children }: { children: ReactNode }) {
  return <p className="ins-note">{children}</p>
}

/** 默认选中第一条（宽屏）；选中的那条被筛掉了就换成第一条。 */
function useAutoSelect(ids: string[], sel: string | null, onSel: (id: string | undefined) => void, ready: boolean) {
  useEffect(() => {
    if (!ready) return
    if (sel && ids.includes(sel)) return
    if (!sel && !wide()) return
    if (ids[0] !== sel) onSel(ids[0])
  }, [ready, sel, ids.join('|')])
}

// ———— 我的判断 ————

type JSource = 'all' | 'think' | 'manual'
type JStatus = 'all' | 'due' | 'fb'
type JSort = 'new' | 'evidence'

function JudgmentsTab({ d, sel, think, onSel }: { d: Data; sel: string | null; think: string | null; onSel: (id: string | undefined) => void }) {
  const [q, setQ] = useState('')
  const [source, setSource] = useState<JSource>('all')
  const [status, setStatus] = useState<JStatus>('all')
  const [sort, setSort] = useState<JSort>('new')
  const all = d.judgments.data ?? []
  // 从思考讨论的「在「观点」里看」来：选中那次讨论记下的判断
  useEffect(() => {
    const j = think && all.find((x) => x.thinkId === think)
    if (j && j.id !== sel) onSel(j.id)
  }, [think, all.length])
  const list = all
    .filter((j) => source === 'all' || j.source === source)
    .filter((j) => status === 'all' || (status === 'due' ? !j.feedback.length : j.feedback.length > 0))
    .filter((j) => !q.trim() || `${j.topic} ${j.reasoning}`.includes(q.trim()))
    .sort((a, b) => (sort === 'evidence' ? b.evidence.length - a.evidence.length : 0) || b.createdAt.localeCompare(a.createdAt))
  useAutoSelect(list.map((j) => j.id), think ? null : sel, onSel, d.judgments.isSuccess && !think)
  const current = all.find((j) => j.id === sel)

  if (d.judgments.isError) return <Note>观点暂时连不上：{d.judgments.error.message}</Note>
  return (
    <>
      <Toolbar q={q} onQ={setQ} count={`${list.length} 条`}>
        <FilterMenu label="来源" value={source} onChange={setSource} options={[['all', '全部来源'], ['think', '思考讨论'], ['manual', '自己写的']]} />
        <FilterMenu label="状态" value={status} onChange={setStatus} options={[['all', '全部状态'], ['due', '待反馈'], ['fb', '有反馈']]} />
        <FilterMenu label="排序" value={sort} onChange={setSort} options={[['new', '最新'], ['evidence', '依据最多']]} />
      </Toolbar>
      <MasterDetail
        label="判断列表"
        hasSel={!!current}
        onBack={() => onSel(undefined)}
        list={
          <>
            {d.judgments.isPending && <Note>正在打开…</Note>}
            {d.judgments.isSuccess && !all.length && <p className="ins-empty">还没有判断。思考讨论定稿时会自动记一条；也可以点右上角「写一条判断」。</p>}
            {all.length > 0 && !list.length && <p className="ins-empty">没有符合的判断</p>}
            {list.map((j) => (
              <button key={j.id} type="button" className="jrow" aria-pressed={j.id === sel} onClick={() => onSel(j.id)}>
                <b>{j.topic}</b>
                <span className="cl">{j.reasoning}</span>
                <span className="m">
                  <span>{j.source === 'think' ? '思考讨论' : '自己写的'}</span>
                  <span>{day(j.createdAt)}</span>
                  <span>依据 {j.evidence.length}</span>
                  <span>反馈 {j.feedback.length}</span>
                  {!j.feedback.length && <span className="due">待反馈</span>}
                </span>
              </button>
            ))}
          </>
        }
        detail={current ? <JudgmentDetail key={current.id} j={current} /> : <Note>选左边的一条</Note>}
      />
    </>
  )
}

function JudgmentDetail({ j }: { j: Judgment }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { message, modal } = AntApp.useApp()
  const remove = useMutation({
    mutationFn: () => deleteJudgment(j.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['insight-judgments'] })
      qc.invalidateQueries({ queryKey: ['insight-relations'] })
      qc.invalidateQueries({ queryKey: ['insight-topic'] })
      qc.invalidateQueries({ queryKey: ['knowledge'] })
      qc.invalidateQueries({ queryKey: ['folders'] })
      void message.success('删掉了')
    },
    onError: (e) => void message.error(e.message),
  })
  const { open } = useDocPreview()
  const [fb, setFb] = useState('')
  const detail = useQuery({ queryKey: ['insight-judgment', j.id], queryFn: () => getJudgment(j.id) })
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['insight-judgments'] })
    qc.invalidateQueries({ queryKey: ['insight-judgment', j.id] })
  }
  const add = useMutation({
    mutationFn: () => addFeedback(j.id, fb.trim()),
    onSuccess: (next) => {
      setFb('')
      refresh()
      if (next.saveError) message.warning(`反馈记下了，但没能更新知识库里那一篇：${next.saveError}`)
      else message.success('实践反馈记下了')
    },
    onError: (e) => void message.error(e.message),
  })
  const sync = useMutation({ mutationFn: () => syncJudgment(j.id), onSuccess: refresh, onError: (e) => void message.error(e.message) })
  return (
    <article className="dt" aria-label={j.topic}>
      <p className="dt-small">
        {j.source === 'think' ? '思考讨论' : '自己写的'} · {day(j.createdAt)}
      </p>
      <h2 className="dt-title">{j.topic}</h2>
      <h3 className="dt-label">我的看法</h3>
      <p className="dt-main">{j.reasoning}</p>
      {j.saveError && (
        <div className="failed" role="alert">
          <span>没能存进知识库：{j.saveError}</span>
          <Pill size="sm" icon={<RotateCcw />} onClick={() => sync.mutate()} disabled={sync.isPending}>
            再试一次
          </Pill>
        </div>
      )}
      <h3 className="dt-label">依据 · {j.evidence.length}</h3>
      {j.evidence.length ? (
        <ol className="dt-list">
          {j.evidence.map((e, i) => (
            <li key={`${e.kind}:${e.id}`}>
              <span className="sk-cite" aria-hidden="true">
                {i + 1}
              </span>
              <span>
                {e.text}
                <OpenDoc knowledgeId={e.knowledgeId} kbId={e.kbId}>
                  {cleanTitle(e.title)}
                </OpenDoc>
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <Note>写的时候没选依据。</Note>
      )}
      {(detail.data?.relations.length ?? 0) > 0 && <Relations relations={detail.data!.relations} onChanged={refresh} />}
      <h3 className="dt-label">实践反馈 · {j.feedback.length}</h3>
      {j.feedback.length ? (
        <ul className="dt-fb">
          {j.feedback.map((f, i) => (
            <li key={i}>
              <i aria-hidden="true" />
              <span>
                <small>{day(f.at)}</small>
                {f.text}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <Note>还没有。用过以后回来写一句：结果怎样、要不要改。判断本身不会改，反馈按时间追加。</Note>
      )}
      <form
        className="oneline dt-oneline"
        onSubmit={(e) => {
          e.preventDefault()
          if (fb.trim()) add.mutate()
        }}
      >
        <label className="sr-only" htmlFor={`fb-${j.id}`}>
          写实践反馈
        </label>
        <input id={`fb-${j.id}`} value={fb} maxLength={2000} placeholder="写一条实践反馈…" onChange={(e) => setFb(e.target.value)} />
        <IconButton label="追加实践反馈" type="submit" disabled={!fb.trim() || add.isPending}>
          <ArrowUp strokeWidth={2.2} />
        </IconButton>
      </form>
      <div className="dt-acts">
        <Pill size="sm" className="think-go" icon={<Lightbulb />} onClick={() => navigate('/', { state: { mode: 'think', topic: j.topic } })}>
          拿它再讨论一次
        </Pill>
        {j.saved && (
          <Pill size="sm" variant="ghost" icon={<BookOpen />} onClick={() => open(j.saved!.knowledgeId, j.saved!.kbId)}>
            在知识库里打开
          </Pill>
        )}
        <span className="grow" />
        <button
          type="button"
          className="dt-link dt-danger"
          disabled={remove.isPending}
          onClick={() =>
            modal.confirm({
              title: '删掉这条判断？',
              content:
                j.source === 'think'
                  ? '连着它的关系一起删；知识库「思考」里的判决书也会删掉，以后不再作为「过去的你」出场。这次讨论的过程和分歧地图还在。删了不能恢复。'
                  : '连着它的关系一起删；知识库「思考」里的「我的判断」那一篇也会删掉，以后不再作为「过去的你」出场。删了不能恢复。',
              okText: '删掉',
              okButtonProps: { danger: true },
              cancelText: '取消',
              onOk: () => remove.mutateAsync(),
            })
          }
        >
          删掉
        </button>
      </div>
    </article>
  )
}

function Relations({ relations, onChanged }: { relations: Relation[]; onChanged?: () => void }) {
  const qc = useQueryClient()
  const { message } = AntApp.useApp()
  const drop = useMutation({
    mutationFn: unrelate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['insight-topic'] })
      qc.invalidateQueries({ queryKey: ['insight-relations'] })
      onChanged?.()
      message.success('关系撤销了')
    },
  })
  return (
    <div className="rels" role="region" aria-label="你确认的关系">
      <h3 className="dt-label">关系 · {relations.length}</h3>
      <ul>
        {relations.map((r) => (
          <li key={r.id}>
            <span className="rel-end">{r.source.label}</span>
            <span className="rel-type" data-type={r.type}>
              {RELATION_LABEL[r.type]}
            </span>
            <span className="rel-end">{r.target.label}</span>
            <IconButton label="撤销这条关系" className="rel-drop" onClick={() => drop.mutate(r.id)}>
              <X width={12} height={12} />
            </IconButton>
            <small>理由：{r.reason}</small>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ———— 观点 ————

type CKind = 'all' | ClaimKind
type CSource = 'all' | 'ai' | 'user'
type CGroup = 'doc' | 'topic' | 'none'
const OPEN_GROUPS = 3

function ClaimsTab(props: {
  d: Data
  sel: string | null
  onSel: (id: string | undefined) => void
  inDoc: string | null
  onInDoc: (id: string | undefined) => void
  extract: string | null
  onExtract: (id: string | undefined) => void
  onTopic: (t: string) => void
  picked: (id: string) => boolean
  toggle: (e: Evidence) => void
  onCompose: (c: Claim) => void
}) {
  const { d, sel, onSel, inDoc } = props
  const [q, setQ] = useState('')
  const [kind, setKind] = useState<CKind>('all')
  const [source, setSource] = useState<CSource>('all')
  const [group, setGroup] = useState<CGroup>('doc')
  const [toggled, setToggled] = useState<Set<string>>(new Set())
  // 分组默认展开前几组；「全部收起 / 全部展开」以后，各组再单独点
  const [fold, setFold] = useState<'auto' | 'open' | 'closed'>('auto')
  const all = d.claims.data ?? []
  const text = q.trim()
  const list = all.filter(
    (c) =>
      (!inDoc || c.knowledgeId === inDoc) &&
      (kind === 'all' || c.kind === kind) &&
      (source === 'all' || (source === 'user' ? c.source === 'user' : c.source !== 'user')) &&
      (!text || `${c.text} ${c.quote} ${c.concepts.join(' ')} ${c.docTitle}`.includes(text)),
  )
  const groups = useMemo(() => {
    if (group === 'none') return [{ key: '', name: '', items: list }]
    const by = new Map<string, { key: string; name: string; items: Claim[] }>()
    for (const c of list) {
      const key = group === 'doc' ? c.knowledgeId : c.concepts[0] ?? ''
      const name = group === 'doc' ? cleanTitle(c.docTitle) : c.concepts[0] ?? '没有主题'
      const g = by.get(key) ?? { key, name, items: [] }
      g.items.push(c)
      by.set(key, g)
    }
    // 资料里按原文顺序，组按条数
    for (const g of by.values()) if (group === 'doc') g.items.sort((a, b) => a.chunkIndex - b.chunkIndex || a.createdAt.localeCompare(b.createdAt))
    return [...by.values()].sort((a, b) => b.items.length - a.items.length || a.name.localeCompare(b.name))
  }, [list, group])
  const isOpen = (key: string, i: number) => (fold === 'open' ? true : fold === 'closed' ? false : i < OPEN_GROUPS) !== toggled.has(key)
  const anyOpen = groups.some((g, i) => isOpen(g.key, i))
  const visible = groups.flatMap((g, i) => (isOpen(g.key, i) ? g.items : []))
  useAutoSelect(visible.map((c) => c.id), sel, onSel, d.claims.isSuccess && !props.extract)
  const current = all.find((c) => c.id === sel)
  const inTitle = inDoc ? cleanTitle(all.find((c) => c.knowledgeId === inDoc)?.docTitle ?? '这篇资料') : ''

  if (d.claims.isError) return <Note>观点暂时连不上：{d.claims.error.message}</Note>
  return (
    <>
      <Toolbar q={q} onQ={setQ} count={`${list.length} 条`}>
        {inDoc && (
          <span className="sk-chip ins-in">
            只看《{inTitle}》
            <IconButton label="看全部资料的观点" style={{ width: 18, height: 18 }} onClick={() => props.onInDoc(undefined)}>
              <X width={12} height={12} />
            </IconButton>
          </span>
        )}
        <FilterMenu label="类型" value={kind} onChange={setKind} options={[['all', '全部类型'], ['fact', '事实'], ['opinion', '作者观点'], ['inference', '推断']]} />
        <FilterMenu label="来源" value={source} onChange={setSource} options={[['all', '全部来源'], ['ai', 'AI 整理'], ['user', '我记下的']]} />
        <FilterMenu
          label="分组"
          value={group}
          onChange={(g) => {
            setGroup(g)
            setToggled(new Set())
            setFold('auto')
          }}
          options={[['doc', '按资料分组'], ['topic', '按主题分组'], ['none', '不分组']]}
        />
        {group !== 'none' && groups.length > 0 && (
          <Pill
            size="sm"
            variant="ghost"
            onClick={() => {
              setFold(anyOpen ? 'closed' : 'open')
              setToggled(new Set())
            }}
          >
            {anyOpen ? '全部收起' : '全部展开'}
          </Pill>
        )}
        <AttachPicker picked={[]} max={1} label="整理一篇资料" onPick={(k) => props.onExtract(k.id)} />
      </Toolbar>
      <MasterDetail
        label="观点列表"
        hasSel={!!current || !!props.extract}
        onBack={() => (props.extract ? props.onExtract(undefined) : onSel(undefined))}
        list={
          <>
            {d.claims.isPending && <Note>正在打开…</Note>}
            {d.claims.isSuccess && !all.length && <p className="ins-empty">还没有观点。读资料时选中一句点「记为观点」，或者用上面的「整理一篇资料」让 AI 整理。</p>}
            {all.length > 0 && !list.length && <p className="ins-empty">没有符合的观点</p>}
            {groups.map((g, i) =>
              group === 'none' ? (
                <ClaimRows key="all" items={g.items} {...props} />
              ) : (
                <section key={g.key} className="grp" aria-label={g.name}>
                  <button type="button" className="grp-h" aria-expanded={isOpen(g.key, i)} onClick={() => setToggled((t) => new Set(t.has(g.key) ? [...t].filter((x) => x !== g.key) : [...t, g.key]))}>
                    <ChevronRight className="chev" width={14} height={14} />
                    <span className="nm">{g.name}</span>
                    <span className="ct">{g.items.length}</span>
                  </button>
                  {isOpen(g.key, i) && <ClaimRows items={g.items} {...props} />}
                </section>
              ),
            )}
          </>
        }
        detail={
          props.extract ? (
            <ExtractPanel key={props.extract} id={props.extract} onDone={(id) => props.onInDoc(id)} />
          ) : current ? (
            <ClaimDetail key={current.id} c={current} {...props} />
          ) : (
            <Note>选左边的一条</Note>
          )
        }
      />
    </>
  )
}

function ClaimRows({ items, d, sel, onSel }: { items: Claim[]; d: Data; sel: string | null; onSel: (id: string) => void }) {
  const askDelete = useDeleteClaim()
  return (
    <div className="grp-body">
      {items.map((c) => {
        const near = d.near.get(c.id)?.length ?? 0
        const cited = d.cited.get(c.id) ?? 0
        return (
          <div key={c.id} className="crow-w">
          <button type="button" className="crow" aria-pressed={c.id === sel} onClick={() => onSel(c.id)}>
            <span className="tx">{c.text}</span>
            <span className="side-m">
              <span className="claim-kind" data-kind={c.kind}>
                {CLAIM_KIND_LABEL[c.kind]}
              </span>
              {c.source === 'user' && <span className="mine">我记下的</span>}
              {near > 0 && <span className="dup">相近 {near}</span>}
              {cited > 0 && <span className="used">被 {cited} 个判断引用</span>}
            </span>
          </button>
          <IconButton label={`删掉「${c.text.slice(0, 20)}」`} className="crow-del" onClick={() => askDelete(c)}>
            <Trash2 width={14} height={14} />
          </IconButton>
          </div>
        )
      })}
    </div>
  )
}

function ClaimDetail({ c, d, onSel, onTopic, picked, toggle, onCompose }: { c: Claim; d: Data; onSel: (id: string) => void; onTopic: (t: string) => void; picked: (id: string) => boolean; toggle: (e: Evidence) => void; onCompose: (c: Claim) => void }) {
  const qc = useQueryClient()
  const { message, modal } = AntApp.useApp()
  const [relating, setRelating] = useState(false)
  const rels = useQuery({ queryKey: ['insight-relations', 'claim', c.id], queryFn: () => listRelations({ kind: 'claim', id: c.id }) })
  const near = (d.near.get(c.id) ?? []).map((id) => d.claims.data?.find((x) => x.id === id)).filter((x): x is Claim => !!x)
  const changed = () => {
    qc.invalidateQueries({ queryKey: ['insight-claims'] })
    qc.invalidateQueries({ queryKey: ['insight-topics'] })
    qc.invalidateQueries({ queryKey: ['insight-topic'] })
    qc.invalidateQueries({ queryKey: ['insight-relations'] })
    qc.invalidateQueries({ queryKey: ['doc-insights', c.knowledgeId] })
  }
  const merge = useMutation({
    mutationFn: () => mergeClaims(c.id, near.map((x) => x.id)),
    onSuccess: (r) => {
      changed()
      void message.success(`并好了：${r.merged + 1} 条成了一条${r.relations ? `，${r.relations} 条关系改连到这一条` : ''}`)
    },
    onError: (e) => void message.error(e.message),
  })
  const askDelete = useDeleteClaim(c.knowledgeId)
  const on = picked(c.id)
  const kb = `/kb/${c.kbId}/d/${c.knowledgeId}?hl=${encodeURIComponent(c.quote.slice(0, 80))}`
  return (
    <article className="dt" aria-label={c.text}>
      <div className="dt-meta">
        <span className="claim-kind" data-kind={c.kind}>
          {CLAIM_KIND_LABEL[c.kind]}
        </span>
        <span className="dt-small">{c.source === 'user' ? '我记下的' : 'AI 整理'}</span>
        {c.speaker && <span className="dt-small">· {c.speaker}</span>}
      </div>
      <p className="dt-main dt-claim">{c.text}</p>
      {c.condition && <p className="dt-small">适用条件：{c.condition}</p>}
      <h3 className="dt-label">原文</h3>
      <blockquote className="dt-quote">「{c.quote}」</blockquote>
      <p className="dt-small dt-at">
        <Link to={kb}>《{cleanTitle(c.docTitle)}》 · 在知识库里定位 →</Link>
      </p>
      {c.concepts.length > 0 && (
        <>
          <h3 className="dt-label">主题</h3>
          <div className="dt-meta">
            {c.concepts.map((t) => (
              <button key={t} type="button" className="ins-tag" onClick={() => onTopic(t)}>
                {t}
              </button>
            ))}
          </div>
        </>
      )}
      {near.length > 0 && (
        <>
          <h3 className="dt-label">相近的观点 · {near.length}</h3>
          <ul className="dt-list">
            {near.map((x) => (
              <li key={x.id}>
                <button type="button" className="dt-link" onClick={() => onSel(x.id)}>
                  {x.text}
                </button>
              </li>
            ))}
          </ul>
          <div className="dt-acts">
            <Pill
              size="sm"
              disabled={merge.isPending}
              onClick={() =>
                modal.confirm({
                  title: '合并成一条？',
                  content: `留下这一条，把相近的 ${near.length} 条并进来：主题合在一起，连着它们的关系改连到这一条，并掉的那几条删掉。判断里抄下的依据不受影响。`,
                  okText: '合并',
                  cancelText: '取消',
                  onOk: () => merge.mutateAsync(),
                })
              }
            >
              合并成一条
            </Pill>
          </div>
        </>
      )}
      {(rels.data?.length ?? 0) > 0 ? <Relations relations={rels.data!} /> : <h3 className="dt-label">关系</h3>}
      {relating ? (
        <RelateForm from={c} d={d} onDone={() => setRelating(false)} />
      ) : (
        <button type="button" className="dt-link dt-add" onClick={() => setRelating(true)}>
          ＋ 关联另一条（支持 / 质疑 / 修正 / 依赖）
        </button>
      )}
      <div className="dt-acts">
        <Pill size="sm" variant={on ? 'selected' : 'primary'} icon={on ? <Check /> : <Plus />} aria-pressed={on} onClick={() => toggle(pickClaim(c) as Evidence)}>
          {on ? '已选为依据' : '选为依据'}
        </Pill>
        <Pill size="sm" variant="ghost" onClick={() => onCompose(c)}>
          用它写判断
        </Pill>
        <span className="grow" />
        <button type="button" className="dt-link dt-danger" onClick={() => askDelete(c)}>
          删掉
        </button>
      </div>
    </article>
  )
}

/** 关联另一条：从观点和判断里找一条，选关系、写一句理由。 */
function RelateForm({ from, d, onDone }: { from: Claim; d: Data; onDone: () => void }) {
  const qc = useQueryClient()
  const { message } = AntApp.useApp()
  const [q, setQ] = useState('')
  const [to, setTo] = useState<{ kind: 'claim' | 'judgment'; id: string; label: string } | null>(null)
  const [type, setType] = useState<RelationType>('SUPPORTS')
  const [reason, setReason] = useState('')
  const t = q.trim()
  const hits = [
    ...(d.judgments.data ?? []).map((j) => ({ kind: 'judgment' as const, id: j.id, label: j.topic, hay: `${j.topic} ${j.reasoning}` })),
    ...(d.claims.data ?? []).filter((c) => c.id !== from.id).map((c) => ({ kind: 'claim' as const, id: c.id, label: c.text, hay: `${c.text} ${c.quote} ${c.concepts.join(' ')}` })),
  ]
    .filter((x) => !t || x.hay.includes(t))
    .slice(0, 8)
  const link = useMutation({
    mutationFn: () => relate({ source: { kind: 'claim', id: from.id }, target: { kind: to!.kind, id: to!.id }, type, reason: reason.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['insight-relations'] })
      qc.invalidateQueries({ queryKey: ['insight-topic'] })
      qc.invalidateQueries({ queryKey: ['insight-judgment'] })
      void message.success(`已确认：${RELATION_LABEL[type]}`)
      onDone()
    },
    onError: (e) => void message.error(e.message),
  })
  return (
    <form
      className="dt-relate"
      aria-label="关联另一条"
      onSubmit={(e) => {
        e.preventDefault()
        if (to && reason.trim()) link.mutate()
      }}
    >
      {to ? (
        <div className="dt-to">
          <span className="claim-kind" data-kind={to.kind}>
            {to.kind === 'claim' ? '观点' : '判断'}
          </span>
          <span className="t">{to.label}</span>
          <IconButton label="换一条" onClick={() => setTo(null)}>
            <X width={12} height={12} />
          </IconButton>
        </div>
      ) : (
        <>
          <label className="sk-search">
            <Search width={15} height={15} />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="找要关联的观点或判断" aria-label="找要关联的观点或判断" />
          </label>
          <ul className="dt-hits" role="listbox" aria-label="可以关联的">
            {hits.map((h) => (
              <li key={`${h.kind}:${h.id}`}>
                <button type="button" role="option" aria-selected={false} onClick={() => setTo(h)}>
                  <span className="claim-kind" data-kind={h.kind}>
                    {h.kind === 'claim' ? '观点' : '判断'}
                  </span>
                  <span className="t">{h.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      <p className="ins-note">这一条对它是什么关系？关系帮你组织依据，不证明结论对。</p>
      <Segmented label="关系" value={type} onChange={setType} items={(Object.keys(RELATION_LABEL) as RelationType[]).map((v) => ({ value: v, label: RELATION_LABEL[v] }))} />
      <label className="sr-only" htmlFor="rel-reason">
        理由
      </label>
      <input id="rel-reason" className="dt-input" value={reason} maxLength={500} placeholder="一句话说清楚为什么" onChange={(e) => setReason(e.target.value)} />
      <div className="dt-acts">
        <Pill type="submit" size="sm" variant="primary" disabled={!to || !reason.trim() || link.isPending}>
          确认关系
        </Pill>
        <Pill size="sm" variant="ghost" onClick={onDone}>
          取消
        </Pill>
      </div>
    </form>
  )
}

/** 整理一篇资料：进度、整理、取消；整理好的在列表里（筛到这一篇）。 */
function ExtractPanel({ id, onDone }: { id: string; onDone: (id: string) => void }) {
  const qc = useQueryClient()
  const { message } = AntApp.useApp()
  const meta = useQuery({ queryKey: ['insight-meta'], queryFn: insightsMeta })
  const q = useQuery({
    queryKey: ['insight-doc', id],
    queryFn: () => docInsights(id),
    refetchInterval: (query) => (query.state.data?.job?.status === 'running' ? 1000 : false),
  })
  const running = q.data?.job?.status === 'running'
  // 整理完（或者整理出了新的）：列表、主题、今天的次数跟着刷新
  useEffect(() => {
    if (running) return
    qc.invalidateQueries({ queryKey: ['insight-meta'] })
    qc.invalidateQueries({ queryKey: ['insight-claims'] })
    qc.invalidateQueries({ queryKey: ['insight-topics'] })
  }, [running, q.data?.claims.length, q.data?.job?.status, qc])
  const start = useMutation({
    mutationFn: () => extractClaims(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['insight-doc', id] }),
    onError: (e) => void message.error(e.message),
  })
  const stop = useMutation({ mutationFn: (jobId: string) => cancelJob(jobId), onSuccess: () => qc.invalidateQueries({ queryKey: ['insight-doc', id] }) })
  if (q.isPending) return <Note>正在打开…</Note>
  if (q.isError) return <Note>没能打开这篇资料：{q.error.message}</Note>
  const x = q.data
  const left = x.totalChunks - x.processedChunks
  const next = Math.min(left, meta.data?.limits.defaultChunks ?? 5, meta.data?.remainingToday ?? 0)
  const job = x.job
  return (
    <section className="dt" aria-label={`整理《${cleanTitle(x.doc.title)}》`}>
      <p className="dt-small">整理观点</p>
      <h2 className="dt-title">{cleanTitle(x.doc.title)}</h2>
      <p className="dt-small">
        共 {x.totalChunks} 段 · 已整理 {x.processedChunks} 段 · {x.claims.length} 条观点
      </p>
      {running && job ? (
        <div className="ins-job" role="status">
          <span className="live" />
          正在整理第 {Math.min(job.done + 1, job.total)} / {job.total} 段，已整理出 {job.claims} 条
          <span className="grow" />
          <Pill size="sm" onClick={() => stop.mutate(job.id)} disabled={stop.isPending}>
            取消
          </Pill>
        </div>
      ) : left > 0 ? (
        <div className="ins-job">
          <Pill variant="primary" icon={<Lightbulb />} onClick={() => start.mutate()} disabled={start.isPending || next <= 0}>
            整理观点
          </Pill>
          <span className="ins-note">
            {next > 0 ? `接下来 ${next} 段，每段一次模型调用 · 今天还能用 ${meta.data?.remainingToday ?? '…'} 次` : `今天的 ${meta.data?.limits.callsPerDay ?? 20} 次整理已经用完了，明天再来`}
          </span>
        </div>
      ) : (
        <Note>这篇资料的每一段都整理过了。</Note>
      )}
      {job && !running && job.status !== 'done' && (
        <p className="ins-note" role="alert">
          {job.status === 'cancelled' ? '上次整理取消了' : job.status === 'interrupted' ? '上次整理被打断了' : '上次整理没做完'}
          {job.error ? `：${job.error}` : ''}。整理好的都留着。
        </p>
      )}
      {job && !running && job.status === 'done' && (job.dropped > 0 || job.failedChunks > 0) && (
        <Note>
          上次整理：{job.dropped > 0 ? `${job.dropped} 条引用对不上原文，已丢掉` : ''}
          {job.dropped > 0 && job.failedChunks > 0 ? '；' : ''}
          {job.failedChunks > 0 ? `${job.failedChunks} 段没整理成，下次再试` : ''}。
        </Note>
      )}
      {x.claims.length > 0 && (
        <div className="dt-acts">
          <Pill size="sm" onClick={() => onDone(id)}>
            看这篇的 {x.claims.length} 条观点
          </Pill>
        </div>
      )}
    </section>
  )
}

// ———— 主题 ————

type TSort = 'n' | 'az'

function TopicsTab({ d, sel, onSel, onClaim, onCompose }: { d: Data; sel: string | null; onSel: (t: string | undefined) => void; onClaim: (id: string) => void; onCompose: (topic: string) => void }) {
  const [q, setQ] = useState('')
  const [sort, setSort] = useState<TSort>('n')
  const all = d.topics.data ?? []
  const list = all.filter((t) => !q.trim() || t.name.includes(q.trim())).sort((a, b) => (sort === 'az' ? a.name.localeCompare(b.name, 'zh-CN') : b.claims - a.claims || b.docs - a.docs))
  useAutoSelect(list.map((t) => t.name), sel, onSel, d.topics.isSuccess)
  const max = Math.max(1, ...all.map((t) => t.claims))
  if (d.topics.isError) return <Note>观点暂时连不上：{d.topics.error.message}</Note>
  return (
    <>
      <Toolbar q={q} onQ={setQ} count={`${list.length} 个`}>
        <FilterMenu label="排序" value={sort} onChange={setSort} options={[['n', '观点最多'], ['az', '按名字']]} />
      </Toolbar>
      <MasterDetail
        label="主题列表"
        hasSel={!!sel}
        onBack={() => onSel(undefined)}
        list={
          <>
            {d.topics.isPending && <Note>正在打开…</Note>}
            {d.topics.isSuccess && !all.length && <p className="ins-empty">还没有主题。主题来自 AI 整理出的观点：在「观点」里整理一篇资料。</p>}
            {list.map((t) => (
              <button key={t.name} type="button" className="trow" aria-pressed={t.name === sel} aria-label={`${t.name}：${t.claims} 条观点、${t.docs} 篇资料`} onClick={() => onSel(t.name)}>
                <b>{t.name}</b>
                <span className="bar" aria-hidden="true">
                  <i style={{ width: `${(t.claims / max) * 100}%` }} />
                </span>
                <span className="n">
                  {t.claims} 条 · {t.docs} 篇
                </span>
              </button>
            ))}
          </>
        }
        detail={sel ? <TopicDetail key={sel} name={sel} onClaim={onClaim} onCompose={onCompose} /> : <Note>选左边的一个主题</Note>}
      />
    </>
  )
}

function TopicDetail({ name, onClaim, onCompose }: { name: string; onClaim: (id: string) => void; onCompose: (topic: string) => void }) {
  const navigate = useNavigate()
  const t = useQuery({ queryKey: ['insight-topic', name], queryFn: () => getTopic(name) })
  if (t.isPending) return <Note>正在打开…</Note>
  if (t.isError) return <Note>{t.error.message}</Note>
  const x = t.data
  return (
    <article className="dt" aria-label={`主题：${x.name}`}>
      <p className="dt-small">主题</p>
      <h2 className="dt-title">{x.name}</h2>
      <p className="dt-small">
        {x.claims.length} 条观点 · 来自 {x.docs.length} 篇资料
      </p>
      <h3 className="dt-label">这个主题下的观点</h3>
      <ul className="dt-list">
        {x.claims.map((c) => (
          <li key={c.id}>
            <span className="claim-kind" data-kind={c.kind}>
              {CLAIM_KIND_LABEL[c.kind]}
            </span>
            <button type="button" className="dt-link" onClick={() => onClaim(c.id)}>
              {c.text}
            </button>
          </li>
        ))}
      </ul>
      {x.relations.length > 0 && <Relations relations={x.relations} />}
      <div className="dt-acts">
        <Pill size="sm" className="think-go" icon={<Lightbulb />} onClick={() => navigate('/', { state: { mode: 'think', topic: `关于「${x.name}」，我该怎么做？` } })}>
          就这个主题讨论
        </Pill>
        <Pill size="sm" variant="ghost" onClick={() => onCompose(x.name)}>
          写一条判断
        </Pill>
      </div>
    </article>
  )
}

// ———— 写一条判断 ————

function ComposeJudgment({ draft, onDraft, onBack, onPickMore, onSaved }: { draft: Draft; onDraft: (d: Draft) => void; onBack: () => void; onPickMore: () => void; onSaved: (j: Judgment) => void }) {
  const qc = useQueryClient()
  const { message } = AntApp.useApp()
  const [searching, setSearching] = useState(false)
  const reasonBox = useRef<HTMLTextAreaElement>(null)
  const voice = useDictation({ value: draft.reasoning, onChange: (v) => onDraft({ ...draft, reasoning: v }), inputRef: reasonBox })
  const save = useMutation({
    mutationFn: () => createJudgment({ topic: draft.topic.trim(), reasoning: draft.reasoning.trim(), evidence: draft.evidence.map((e) => ({ kind: e.kind, id: e.id, knowledgeId: e.knowledgeId, kbId: e.kbId, title: e.title, text: e.text })) }),
    onSuccess: (j) => {
      qc.invalidateQueries({ queryKey: ['insight-judgments'] })
      if (j.saveError) message.warning(`判断记下了，但没能存进知识库：${j.saveError}`)
      else message.success('判断记下了，也存进了知识库「思考」')
      onSaved(j)
    },
    onError: (e) => void message.error(e.message),
  })
  const drop = (e: Evidence) => onDraft({ ...draft, evidence: draft.evidence.filter((x) => keyOf(x) !== keyOf(e)) })
  return (
    <div className="ins-compose">
      <Pill size="sm" variant="ghost" icon={<ArrowLeft />} onClick={onBack}>
        观点
      </Pill>
      <label className="sr-only" htmlFor="judge-topic">
        你想判断什么
      </label>
      <textarea id="judge-topic" className="motion" rows={2} maxLength={200} value={draft.topic} placeholder="你想判断什么？" onChange={(e) => onDraft({ ...draft, topic: e.target.value })} autoFocus />
      <label className="sr-only" htmlFor="judge-reasoning">
        我的看法
      </label>
      <div className="ins-voice">
        <textarea id="judge-reasoning" ref={reasonBox} className="ins-field" rows={6} maxLength={10000} value={draft.reasoning} placeholder="你怎么看？为什么？（可以点右下角说出来）" onChange={(e) => onDraft({ ...draft, reasoning: e.target.value })} readOnly={voice.active} data-dictating={voice.active} />
        <MicButton d={voice} />
      </div>
      <ol className="evi" aria-label="依据">
        {draft.evidence.map((e, i) => (
          <li key={keyOf(e)}>
            <span className="sk-cite" aria-hidden="true">
              {i + 1}
            </span>
            <span className="t">
              {e.text} · {cleanTitle(e.title)}
            </span>
            <IconButton label={`去掉依据 ${i + 1}`} onClick={() => drop(e)}>
              <X width={13} height={13} />
            </IconButton>
          </li>
        ))}
        <li className="evi-add">
          <button type="button" className="dt-link" onClick={onPickMore}>
            ＋ 从观点里挑
          </button>
          <span aria-hidden="true">·</span>
          <button type="button" className="dt-link" aria-expanded={searching} onClick={() => setSearching((v) => !v)}>
            搜资料
          </button>
        </li>
      </ol>
      {searching && <EvidenceSearch picked={draft.evidence} onAdd={(e) => onDraft({ ...draft, evidence: [...draft.evidence, e] })} />}
      <p className="ins-note">保存后不能改，以后追加实践反馈。会存一份进知识库「思考」，下次思考讨论同类问题时作为「过去的你」出场。</p>
      <div className="dt-acts">
        <Pill variant="primary" disabled={!draft.topic.trim() || !draft.reasoning.trim() || save.isPending || voice.active} onClick={() => save.mutate()}>
          保存
        </Pill>
      </div>
    </div>
  )
}

function EvidenceSearch({ picked, onAdd }: { picked: Evidence[]; onAdd: (e: Evidence) => void }) {
  const [q, setQ] = useState('')
  const [asked, setAsked] = useState('')
  const r = useQuery({ queryKey: ['insight-search', asked], enabled: !!asked, queryFn: () => searchEvidence(asked) })
  const has = (e: Evidence) => picked.some((x) => keyOf(x) === keyOf(e))
  const items: Evidence[] = [...(r.data?.passages ?? []).map((p) => pickPassage(p) as Evidence), ...(r.data?.claims ?? []).map((c) => pickClaim(c) as Evidence)]
  return (
    <div className="evi-search" role="search">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (q.trim()) setAsked(q.trim())
        }}
      >
        <label className="sk-search">
          <Search width={15} height={15} />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="搜资料里的原文和观点" aria-label="找依据的问题" maxLength={500} />
        </label>
      </form>
      {r.isFetching && <Note>正在找…</Note>}
      {r.isError && <Note>没找成：{r.error.message}</Note>}
      {r.data && !items.length && <Note>没找到和「{asked}」相关的</Note>}
      <ul className="evi-hits" aria-label="找到的依据">
        {items.map((e) => (
          <li key={keyOf(e)}>
            <span className="claim-kind" data-kind={e.kind}>
              {e.kind === 'passage' ? '原文' : '观点'}
            </span>
            <span className="t">
              {e.text}
              <small>{cleanTitle(e.title)}</small>
            </span>
            <Pill size="sm" variant={has(e) ? 'selected' : 'soft'} disabled={has(e)} icon={has(e) ? <Check /> : <Plus />} onClick={() => onAdd(e)}>
              {has(e) ? '已加' : '加进来'}
            </Pill>
          </li>
        ))}
      </ul>
    </div>
  )
}
